import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Tag,
  Input,
  Select,
  Modal,
  Form,
  message,
  Tooltip,
  Space,
  Empty,
  Tabs,
  Spin,
  DatePicker,
  Row,
  Col,
  AutoComplete
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  LogoutOutlined,
  YoutubeOutlined,
  GoogleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  DownloadOutlined,
  SyncOutlined,
  FilterOutlined,
  HistoryOutlined,
  VideoCameraOutlined,
  UserOutlined
} from '@ant-design/icons';
import { leaderboardAPI, apiClient } from '../services/api';
import './Verification.css';

const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const Verification = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [isFetching, setIsFetching] = useState(false);
  const [fetchProgressModal, setFetchProgressModal] = useState(false);
  const [fetchStatus, setFetchStatus] = useState({ google: 'idle', youtube: 'idle' });

  // Filter states
  const [availableHashtags, setAvailableHashtags] = useState([]);
  const [selectedHashtag, setSelectedHashtag] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [_, setTotalPages] = useState(0);

  // Sorting states
  const [sortField, setSortField] = useState('scraped_at');
  const [sortOrder, setSortOrder] = useState('descend');

  // Affiliate search states
  const [selectedRole, setSelectedRole] = useState(null);
  const [affiliateOptions, setAffiliateOptions] = useState([]);
  const [searchingAffiliates, setSearchingAffiliates] = useState(false);
  const [selectedAffiliateUnitId, setSelectedAffiliateUnitId] = useState(null);

  // Scraping platform selection
  const [selectedScrapingPlatform, setSelectedScrapingPlatform] = useState('both');

  // Manual entry states
  const [manualEntryModalVisible, setManualEntryModalVisible] = useState(false);
  const [manualEntryForm] = Form.useForm();

  // History states
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load data function with useCallback to fix ESLint warning
  const loadData = useCallback(async (page = currentPage) => {
    setLoading(true);
    try {
      let response;

      // Build query parameters based on filters
      const params = {
        page: page,
        per_page: itemsPerPage,
        sort_by: sortField,
        sort_order: sortOrder === 'descend' ? 'desc' : 'asc'
      };

      if (activeTab === 'pending') {
        params.is_processed = 'false';
      } else {
        params.is_processed = 'true';
        // Add verification_status filter for approved/rejected tabs
        if (activeTab === 'approved') {
          params.verification_status = 'approved';
        } else if (activeTab === 'rejected') {
          params.verification_status = 'rejected';
        }
      }

      // Add filter parameters
      if (selectedHashtag) {
        params.hashtag = selectedHashtag;
      }
      if (selectedPlatform) {
        params.platform = selectedPlatform;
      }
      if (dateRange && dateRange.length === 2) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      if (selectedCampaign) {
        params.campaign_id = selectedCampaign;
      }

      response = await apiClient.get('/scrap-data', { params });

      if (response.data.status === 'success') {
        // Save pagination info from backend
        const pagination = response.data.pagination || {};
        setTotalItems(pagination.total || 0);
        setTotalPages(pagination.total_pages || pagination.pages || 0);
        setCurrentPage(pagination.page || page);

        // Transform backend data to match our table format
        const transformedData = response.data.data.map(item => ({
          id: item.id,
          platform: item.platform,
          video_title: item.content_text || 'No title',
          video_url: item.content_url,
          channel_name: item.author_name || item.author_username,
          author_username: item.author_username,
          upload_date: item.posted_at,
          status: item.verification_status || 'pending',
          crawled_date: item.scraped_at,
          hashtag: item.hashtag,
          content_id: item.content_id,
          likes_count: item.likes_count,
          comments_count: item.comments_count,
          shares_count: item.shares_count,
          affiliates: item.affiliates || [],
          rejection_reason: item.rejection_reason,
          verified_by: item.verified_by,
          verified_at: item.verified_at,
          campaign: item.campaign || null
        }));

        // No need for client-side filtering - backend now filters by verification_status
        setData(transformedData);
      } else {
        message.error('Gagal memuat data');
      }
    } catch (error) {
      message.error('Gagal memuat data dari server');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedHashtag, selectedPlatform, dateRange, selectedCampaign, itemsPerPage, currentPage, sortField, sortOrder]); // Add dependencies

  // Load hashtags on component mount
  useEffect(() => {
    const loadHashtags = async () => {
      try {
        const response = await apiClient.get('/api/hashtags');
        if (response.data.status === 'success') {
          const activeHashtags = response.data.data.filter(h => h.is_active);
          setAvailableHashtags(activeHashtags);
          // Set first active hashtag as default
          if (activeHashtags.length > 0) {
            setSelectedHashtag(activeHashtags[0].text); // Fixed: use .text not .hashtag
          } else {
            // Fallback if no hashtags in database
            setSelectedHashtag('#AI-ITERA');
          }
        }
      } catch (error) {
        console.error('Failed to load hashtags:', error);
        // Fallback to default hashtag if API fails
        setSelectedHashtag('#AI-ITERA');
        setAvailableHashtags([
          { id: 1, text: '#AI-ITERA', is_active: true, enabled_youtube: true, enabled_google: true }
        ]);
      }
    };

    loadHashtags();
  }, []);

  // Load campaigns on component mount
  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const response = await apiClient.get('/api/campaigns', { params: { status: 'active' } });
        if (response.data.status === 'success') {
          setAvailableCampaigns(response.data.data || []);
        }
      } catch (error) {
        console.error('Failed to load campaigns:', error);
        setAvailableCampaigns([]);
      }
    };

    loadCampaigns();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, selectedHashtag, selectedPlatform, dateRange, selectedCampaign]);

  // Load data when dependencies change
  useEffect(() => {
    // Skip auth check if embedded in admin panel
    if (!embedded) {
      // Check if user is logged in (use consistent 'token' key)
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        message.warning('Silakan login terlebih dahulu');
        navigate('/login');
        return;
      }
    }

    loadData();
  }, [navigate, embedded, loadData]);

  const handleVerify = (record) => {
    setSelectedRecord(record);
    form.resetFields();
    setSelectedRole(null);
    setAffiliateOptions([]);
    setSelectedAffiliateUnitId(null);
    setModalVisible(true);
  };

  const handleRoleChange = (value) => {
    setSelectedRole(value);
    form.setFieldsValue({ nim_nip: '', name: '', affiliate_id: '' });
    setAffiliateOptions([]);
    setSelectedAffiliateUnitId(null);
  };

  const handleAffiliateSearch = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setAffiliateOptions([]);
      return;
    }

    try {
      setSearchingAffiliates(true);

      // Fix: Proper type mapping for all three roles
      let type;
      if (selectedRole === 'student') {
        type = 'mahasiswa';
      } else if (selectedRole === 'lecturer') {
        type = 'dosen';
      } else if (selectedRole === 'staff') {
        type = 'tenaga_pendidik';
      } else {
        // Fallback
        setSearchingAffiliates(false);
        return;
      }

      const response = await leaderboardAPI.searchAffiliates(searchText, type, 20);

      if (response.status === 'success' && response.data) {
        const options = response.data.map(affiliate => ({
          value: affiliate.external_id,
          label: `${affiliate.name || affiliate.external_id} (${affiliate.external_id}) - ${affiliate.unit_code || ''}`,
          affiliate: affiliate
        }));
        setAffiliateOptions(options);

        if (options.length === 0) {
          message.info('Tidak ada data ditemukan. Anda dapat mengetik NIP dan nama secara manual.');
        }
      } else {
        setAffiliateOptions([]);
        message.warning('Tidak ada hasil pencarian. Anda dapat mengetik NIP dan nama secara manual.');
      }
    } catch (error) {
      console.error('Error searching affiliates:', error);
      message.error('Gagal mencari data. Anda dapat mengetik NIP dan nama secara manual.');
      setAffiliateOptions([]);
    } finally {
      setSearchingAffiliates(false);
    }
  };

  const handleAffiliateSelect = (value, option) => {
    if (option && option.affiliate) {
      form.setFieldsValue({
        nim_nip: option.affiliate.external_id,
        name: option.affiliate.name
      });
      // Save unit_id for backend validation (required for dosen/tenaga_pendidik)
      setSelectedAffiliateUnitId(option.affiliate.unit_id);
    }
  };

  // Separate handler for manual entry modal autocomplete
  const handleManualEntryAffiliateSelect = (value, option) => {
    if (option && option.affiliate) {
      manualEntryForm.setFieldsValue({
        nim_nip: option.affiliate.external_id,
        name: option.affiliate.name
      });
      // Save unit_id for backend validation (required for dosen/tenaga_pendidik)
      setSelectedAffiliateUnitId(option.affiliate.unit_id);
    }
  };

  const handleApprove = async (values) => {
    try {
      // Prepare affiliate data based on form values
      const affiliateType = values.role === 'student' ? 'mahasiswa' : values.role === 'lecturer' ? 'dosen' : 'tenaga_pendidik';

      const affiliate = {
        type: affiliateType,
        nim_or_id: values.nim_nip,
        name: values.name || ''
      };

      // For dosen and tenaga_pendidik, unit_id is required by backend
      // Students (mahasiswa) have unit auto-detected from NIM pattern
      if (affiliateType !== 'mahasiswa' && selectedAffiliateUnitId) {
        affiliate.unit_id = selectedAffiliateUnitId;
      }

      const affiliateData = {
        scraping_result_id: selectedRecord.id,
        status: 'approved',
        affiliates: [affiliate]
      };

      const response = await apiClient.post('/verification', affiliateData);

      if (response.data.status === 'success') {
        const score = response.data.data.score || 1;
        // Determine the appropriate message based on the source tab
        let messageContent;
        if (activeTab === 'rejected') {
          // Re-approval from rejected tab
          messageContent = `🔄 Status berhasil diubah! Konten sekarang disetujui. ${values.nim_nip} mendapat +${score} poin. Data pindah dari "Ditolak" ke "Disetujui".`;
        } else if (activeTab === 'approved') {
          // Re-verify to change affiliate
          messageContent = `🔄 Affiliate berhasil diubah! ${values.nim_nip} sekarang terhubung dengan konten ini dan mendapat +${score} poin.`;
        } else {
          // Initial approval from pending tab
          messageContent = `✅ Konten berhasil diverifikasi! ${values.nim_nip} mendapat +${score} poin. Data pindah dari "Pending" ke "Disetujui".`;
        }

        message.success({
          content: messageContent,
          duration: 5
        });

        // Close modal and reset form first
        setModalVisible(false);
        form.resetFields();
        setSelectedRole(null); // Reset role selection
        setSelectedAffiliateUnitId(null); // Reset unit id

        // Reload data from backend to ensure consistency
        loadData();
      } else {
        message.error(response.data.message || 'Verifikasi gagal');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Verifikasi gagal');
      console.error(error);
    }
  };

  const handleReject = (record) => {
    setSelectedRecord(record);
    rejectForm.resetFields();
    setRejectModalVisible(true);
  };

  const handleRejectSubmit = async (values) => {
    try {
      const response = await apiClient.post('/verification', {
        scraping_result_id: selectedRecord.id,
        status: 'rejected',
        rejection_reason: values.rejection_reason
      });

      if (response.data.status === 'success') {
        // Check if this is re-verification or initial rejection
        const isReverification = activeTab === 'approved';
        const messageContent = isReverification
          ? '🔄 Status berhasil diubah! Konten sekarang ditolak. Data pindah dari "Disetujui" ke "Ditolak".'
          : '⚠️ Konten berhasil ditolak. Data pindah dari "Pending" ke "Ditolak".';

        message.warning({
          content: messageContent,
          duration: 5
        });

        // Close modal and reset form first
        setRejectModalVisible(false);
        rejectForm.resetFields();

        // Reload data from backend to ensure consistency
        loadData();
      } else {
        message.error(response.data.message || 'Gagal menolak verifikasi');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Gagal menolak verifikasi');
      console.error(error);
    }
  };

  const handleManualEntry = async (values) => {
    try {
      const affiliateType = values.role === 'student' ? 'mahasiswa' : values.role === 'lecturer' ? 'dosen' : 'tenaga_pendidik';

      const affiliate = {
        type: affiliateType,
        nim_or_id: values.nim_nip,
        name: values.name || ''
      };

      if (affiliateType !== 'mahasiswa' && selectedAffiliateUnitId) {
        affiliate.unit_id = selectedAffiliateUnitId;
      }

      const manualEntryData = {
        platform: values.platform || 'youtube',
        content_url: values.content_url,
        hashtag: values.hashtag || selectedHashtag || '#AI-ITERA',
        content_text: values.content_text || '',
        campaign_id: selectedCampaign,
        affiliates: [affiliate]
      };

      const response = await apiClient.post('/api/manual-entry', manualEntryData);

      if (response.data.status === 'success') {
        message.success({
          content: `✅ Video berhasil ditambahkan dan diverifikasi otomatis! ${values.nim_nip} mendapat +1 poin. Data sudah pindah ke tab "Disetujui".`,
          duration: 5
        });
        manualEntryForm.resetFields();
        setManualEntryModalVisible(false);
        setSelectedRole(null); // Reset role selection
        setSelectedAffiliateUnitId(null); // Reset unit id
        loadData(); // Reload data to show new entry
      } else {
        message.error(response.data.message || 'Gagal menambahkan video');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Gagal menambahkan video manual');
      console.error(error);
    }
  };

  const handleViewHistory = async (record) => {
    setSelectedRecord(record);
    setHistoryModalVisible(true);
    setLoadingHistory(true);

    try {
      const response = await apiClient.get(`/api/verification/history?scraping_result_id=${record.id}`);

      if (response.data.status === 'success') {
        setHistoryData(response.data.data || []);
      } else {
        message.error('Gagal memuat history');
        setHistoryData([]);
      }
    } catch (error) {
      message.error('Gagal memuat history verifikasi');
      console.error(error);
      setHistoryData([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleFetchNewData = async () => {
    try {
      if (!selectedHashtag) {
        message.warning('Please select a hashtag first');
        return;
      }

      setIsFetching(true);
      setFetchProgressModal(true);

      // Set loading status based on selected platform
      if (selectedScrapingPlatform === 'both') {
        setFetchStatus({ google: 'loading', youtube: 'loading' });
      } else if (selectedScrapingPlatform === 'google') {
        setFetchStatus({ google: 'loading', youtube: 'idle' });
      } else if (selectedScrapingPlatform === 'youtube') {
        setFetchStatus({ google: 'idle', youtube: 'loading' });
      }

      const platformText = selectedScrapingPlatform === 'both' ? 'Google & YouTube' : selectedScrapingPlatform.toUpperCase();
      message.loading({ content: `Fetching new data from ${platformText} for ${selectedHashtag}...`, key: 'fetch', duration: 0 });

      // Trigger scraping with selected hashtag and platform
      const response = await apiClient.post('/api/scraping/trigger', {
        hashtags: [selectedHashtag],
        platform: selectedScrapingPlatform
      });

      if (response.data.status === 'success') {
        // Update status based on platform
        if (selectedScrapingPlatform === 'both') {
          setFetchStatus({ google: 'success', youtube: 'success' });
        } else if (selectedScrapingPlatform === 'google') {
          setFetchStatus({ google: 'success', youtube: 'idle' });
        } else if (selectedScrapingPlatform === 'youtube') {
          setFetchStatus({ google: 'idle', youtube: 'success' });
        }

        let successMsg = `Data fetch started for ${selectedHashtag}!`;
        if (response.data.warnings) {
          successMsg += ` Note: ${response.data.warnings.message}`;
        }
        message.success({ content: successMsg, key: 'fetch', duration: 5 });

        // Wait a bit for scraping to complete, then reload data
        setTimeout(() => {
          loadData();
          setFetchProgressModal(false);
          setFetchStatus({ google: 'idle', youtube: 'idle' });
        }, 10000); // Wait 10 seconds before refreshing
      } else {
        setFetchStatus({ google: 'error', youtube: 'error' });
        message.error({ content: response.data.message || 'Failed to fetch data', key: 'fetch' });
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setFetchStatus({ google: 'error', youtube: 'error' });
      const errorMsg = error.response?.data?.message || 'Failed to fetch new data';
      message.error({ content: errorMsg, key: 'fetch' });

      // Show invalid hashtags if any
      if (error.response?.data?.invalid_hashtags) {
        console.error('Invalid hashtags:', error.response.data.invalid_hashtags);
      }
    } finally {
      setIsFetching(false);
    }
  };

  const clearFilters = () => {
    setSelectedHashtag(availableHashtags.length > 0 ? availableHashtags[0].text : '#AI-ITERA'); // Fixed: use .text
    setSelectedPlatform(null);
    setDateRange(null);
    setSelectedCampaign(null);
    message.info('Filters cleared');
  };

  // Handle pagination change
  const handleTableChange = (pagination, filters, sorter) => {
    const newPage = pagination.current;
    const newPageSize = pagination.pageSize;

    // Handle sorting
    if (sorter && sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order || 'descend');
    }

    // Update page size if changed
    if (newPageSize !== itemsPerPage) {
      setItemsPerPage(newPageSize);
      setCurrentPage(1); // Reset to first page when page size changes
    } else {
      setCurrentPage(newPage);
      loadData(newPage); // Load new page data
    }
  };

  const handleLogout = () => {
    Modal.confirm({
      title: 'Logout',
      content: 'Apakah Anda yakin ingin keluar?',
      okText: 'Ya',
      cancelText: 'Batal',
      onOk: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        message.success('Logout berhasil');
        navigate('/login');
      }
    });
  };

  const columns = [
    {
      title: 'Content',
      key: 'content',
      render: (_, record) => {
        const platformIcon = record.platform === 'youtube'
          ? <YoutubeOutlined style={{ color: '#FF0000', fontSize: '16px' }} />
          : <GoogleOutlined style={{ color: '#4285F4', fontSize: '16px' }} />;

        return (
          <div style={{ minWidth: 300 }}>
            {/* Platform and Title */}
            <div style={{ display: 'flex', alignItems: 'start', gap: 8, marginBottom: 6 }}>
              {platformIcon}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: '14px', lineHeight: '1.4', marginBottom: 4 }}>
                  {record.video_title || 'No title'}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {record.channel_name}
                </div>
              </div>
            </div>

            {/* Tags: Hashtag & Campaign */}
            <div style={{ marginBottom: 6 }}>
              {record.hashtag && (
                <Tag color="blue" style={{ marginRight: 4, marginBottom: 4 }}>
                  {record.hashtag}
                </Tag>
              )}
              {record.campaign && (
                <Tag color="purple" style={{ marginRight: 4, marginBottom: 4 }}>
                  <VideoCameraOutlined /> {record.campaign.name}
                </Tag>
              )}
            </div>

            {/* Affiliates for approved tab */}
            {activeTab === 'approved' && record.affiliates && record.affiliates.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {record.affiliates.slice(0, 3).map((aff, idx) => (
                  <Tag key={idx} color="green" style={{ marginRight: 4, marginBottom: 4, fontSize: '11px' }}>
                    <UserOutlined /> {aff.name || aff.external_id}
                  </Tag>
                ))}
                {record.affiliates.length > 3 && (
                  <Tag style={{ fontSize: '11px' }}>+{record.affiliates.length - 3} more</Tag>
                )}
              </div>
            )}

            {/* Rejection reason for rejected tab */}
            {activeTab === 'rejected' && record.rejection_reason && (
              <div style={{ marginTop: 6, padding: '4px 8px', background: '#fff1f0', borderLeft: '3px solid #ff4d4f', fontSize: '12px', color: '#cf1322' }}>
                <StopOutlined /> {record.rejection_reason}
              </div>
            )}
          </div>
        );
      },
      width: '40%',
    },
    {
      title: 'Engagement',
      key: 'engagement',
      sorter: true,
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 100 }}>
          <div style={{ fontSize: '13px' }}>
            <span style={{ color: '#ff4d4f' }}>❤️</span> {(record.likes_count || 0).toLocaleString()}
          </div>
          <div style={{ fontSize: '13px' }}>
            <span>💬</span> {(record.comments_count || 0).toLocaleString()}
          </div>
          <div style={{ fontSize: '13px' }}>
            <span>🔄</span> {(record.shares_count || 0).toLocaleString()}
          </div>
        </div>
      ),
      width: '110px',
    },
    {
      title: 'Dates',
      key: 'dates',
      sorter: true,
      render: (_, record) => (
        <div style={{ minWidth: 120 }}>
          <div style={{ fontSize: '12px', marginBottom: 4 }}>
            <div style={{ color: '#666', marginBottom: 2 }}>Posted</div>
            <div style={{ fontWeight: 500 }}>
              {record.posted_at ? new Date(record.posted_at).toLocaleDateString('id-ID', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              }) : '-'}
            </div>
          </div>
          <div style={{ fontSize: '12px', paddingTop: 4, borderTop: '1px dashed #e8e8e8' }}>
            <div style={{ color: '#666', marginBottom: 2 }}>Crawled</div>
            <div style={{ fontWeight: 500 }}>
              {record.scraped_at ? new Date(record.scraped_at).toLocaleDateString('id-ID', {
                day: 'numeric',
                month: 'short'
              }) : '-'}
            </div>
          </div>
        </div>
      ),
      width: '130px',
    },
    {
      title: activeTab === 'pending' ? 'Status' : 'Verification Info',
      key: 'verification_info',
      render: (_, record) => {
        if (activeTab === 'pending') {
          return <Tag icon={<ClockCircleOutlined />} color="orange">Pending</Tag>;
        }

        const statusConfig = {
          approved: { color: 'success', icon: <CheckCircleOutlined />, text: 'Approved' },
          rejected: { color: 'error', icon: <CloseCircleOutlined />, text: 'Rejected' }
        };
        const config = statusConfig[record.status] || statusConfig.approved;

        return (
          <div style={{ minWidth: 140 }}>
            <Tag icon={config.icon} color={config.color} style={{ marginBottom: 6 }}>
              {config.text}
            </Tag>
            {record.verified_by && (
              <div style={{ fontSize: '11px', color: '#666', marginTop: 4 }}>
                <UserOutlined /> {record.verified_by}
              </div>
            )}
            {record.verified_at && (
              <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>
                {new Date(record.verified_at).toLocaleString('id-ID', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            )}
          </div>
        );
      },
      width: '160px',
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => window.open(record.video_url, '_blank')}
            block
          >
            View
          </Button>
          {(activeTab === 'approved' || activeTab === 'rejected') && (
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => handleViewHistory(record)}
              block
            >
              History
            </Button>
          )}
          {activeTab === 'pending' && (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleVerify(record)}
              block
            >
              Verify
            </Button>
          )}
          {activeTab === 'approved' && (
            <>
              <Button
                type="default"
                size="small"
                icon={<SyncOutlined />}
                onClick={() => handleVerify(record)}
                block
                style={{ marginBottom: 4 }}
              >
                Re-verify
              </Button>
              <Button
                danger
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={() => handleReject(record)}
                block
              >
                Reject
              </Button>
            </>
          )}
          {activeTab === 'rejected' && (
            <Tooltip title="Re-verify konten ini dan ubah status dari Ditolak ke Disetujui">
              <Button
                type="primary"
                size="small"
                icon={<SyncOutlined />}
                onClick={() => handleVerify(record)}
                block
                style={{ background: '#14B8A6', borderColor: '#14B8A6' }}
              >
                Re-verify
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
      width: '120px',
    },
  ];

  const filteredData = data.filter(item => {
    const matchesSearch = (item.video_title || '').toLowerCase().includes(searchText.toLowerCase()) ||
                         (item.channel_name || '').toLowerCase().includes(searchText.toLowerCase());
    return matchesSearch;
  });

  // Content that will be shown in both embedded and standalone modes
  const verificationContent = (
    <>
      <div className="card-header">
        <div className="filters-row" style={{ marginBottom: 16 }}>
          <Search
            placeholder="Cari video atau channel..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="search-input"
            style={{ maxWidth: 400 }}
          />

          <Space>
            <Select
              value={itemsPerPage}
              onChange={setItemsPerPage}
              size="large"
              style={{ width: 120 }}
            >
              <Option value={50}>50 items</Option>
              <Option value={100}>100 items</Option>
            </Select>

            <Button
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
              size="large"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>

            <Select
              value={selectedScrapingPlatform}
              onChange={setSelectedScrapingPlatform}
              size="large"
              style={{ width: 150 }}
              disabled={isFetching}
            >
              <Option value="both">
                <Space>
                  <GoogleOutlined />
                  <YoutubeOutlined />
                  Both
                </Space>
              </Option>
              <Option value="google">
                <Space>
                  <GoogleOutlined />
                  Google
                </Space>
              </Option>
              <Option value="youtube">
                <Space>
                  <YoutubeOutlined />
                  YouTube
                </Space>
              </Option>
            </Select>

            <Tooltip title={`Fetch new data from ${selectedScrapingPlatform === 'both' ? 'YouTube & Google' : selectedScrapingPlatform.toUpperCase()} for ${selectedHashtag || 'selected hashtag'}`}>
              <Button
                type="primary"
                icon={isFetching ? <SyncOutlined spin /> : <DownloadOutlined />}
                size="large"
                onClick={handleFetchNewData}
                loading={isFetching}
                disabled={isFetching || !selectedHashtag}
              >
                Fetch New Data
              </Button>
            </Tooltip>

            <Tooltip title="Tambah video manual dan langsung verifikasi">
              <Button
                type="default"
                icon={<CheckCircleOutlined />}
                size="large"
                onClick={() => setManualEntryModalVisible(true)}
                style={{ background: '#14B8A6', color: 'white', borderColor: '#14B8A6' }}
              >
                Tambah Video Manual
              </Button>
            </Tooltip>

            <Button
              icon={<ReloadOutlined />}
              size="large"
              onClick={loadData}
              className="refresh-btn"
            >
              Refresh
            </Button>
          </Space>
        </div>

        {showFilters && (
          <div className="filter-panel" style={{
            padding: '16px',
            background: '#fafafa',
            borderRadius: '8px',
            marginBottom: 16
          }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Hashtag
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select hashtag"
                    value={selectedHashtag}
                    onChange={setSelectedHashtag}
                    size="large"
                    allowClear
                  >
                    {availableHashtags.map(tag => (
                      <Option key={tag.id} value={tag.text}>
                        {tag.text}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Platform
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="All platforms"
                    value={selectedPlatform}
                    onChange={setSelectedPlatform}
                    size="large"
                    allowClear
                  >
                    <Option value="google">
                      <GoogleOutlined style={{ marginRight: 8, color: '#4285F4' }} />
                      Google
                    </Option>
                    <Option value="youtube">
                      <YoutubeOutlined style={{ marginRight: 8, color: '#FF0000' }} />
                      YouTube
                    </Option>
                  </Select>
                </div>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Campaign
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="All campaigns"
                    value={selectedCampaign}
                    onChange={setSelectedCampaign}
                    size="large"
                    allowClear
                  >
                    {availableCampaigns.map(campaign => (
                      <Option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Date Range (Upload Date)
                  </label>
                  <RangePicker
                    style={{ width: '100%' }}
                    value={dateRange}
                    onChange={setDateRange}
                    size="large"
                    format="YYYY-MM-DD"
                  />
                </div>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <div style={{ paddingTop: 30 }}>
                  <Button
                    icon={<CloseCircleOutlined />}
                    onClick={clearFilters}
                    size="large"
                    block
                  >
                    Clear Filters
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'pending',
            label: (
              <span>
                <ClockCircleOutlined />
                Pending
              </span>
            ),
            children: (
              <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                loading={loading}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }}
                pagination={{
                  current: currentPage,
                  pageSize: itemsPerPage,
                  total: totalItems,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                }}
                locale={{
                  emptyText: (
                    <Empty
                      description="Tidak ada data pending"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )
                }}
                className="verification-table"
              />
            )
          },
          {
            key: 'approved',
            label: (
              <span>
                <CheckCircleOutlined />
                Approved
              </span>
            ),
            children: (
              <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                loading={loading}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }}
                pagination={{
                  current: currentPage,
                  pageSize: itemsPerPage,
                  total: totalItems,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                }}
                locale={{
                  emptyText: (
                    <Empty
                      description="Tidak ada data yang disetujui"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )
                }}
                className="verification-table"
              />
            )
          },
          {
            key: 'rejected',
            label: (
              <span>
                <CloseCircleOutlined />
                Rejected
              </span>
            ),
            children: (
              <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                loading={loading}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }}
                pagination={{
                  current: currentPage,
                  pageSize: itemsPerPage,
                  total: totalItems,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                }}
                locale={{
                  emptyText: (
                    <Empty
                      description="Tidak ada data yang ditolak"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )
                }}
                className="verification-table"
              />
            )
          }
        ]}
      />
    </>
  );

  // If embedded in admin panel, return content directly
  if (embedded) {
    return (
      <>
        {verificationContent}

        {/* Verification Modal */}
        <Modal
          title={
            <div className="modal-title">
              <CheckCircleOutlined />
              <span>Verifikasi Video</span>
            </div>
          }
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width={600}
          className="verification-modal"
        >
          {selectedRecord && (
            <div className="modal-content">
              <div className="video-preview">
                <YoutubeOutlined className="preview-icon" />
                <div>
                  <h3>{selectedRecord.video_title}</h3>
                  <p>{selectedRecord.channel_name}</p>
                  <a
                    href={selectedRecord.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Lihat Video <EyeOutlined />
                  </a>
                </div>
              </div>

              {/* Show rejection reason context when reverifying from rejected tab */}
              {activeTab === 'rejected' && selectedRecord.rejection_reason && (
                <div style={{
                  background: '#fff1f0',
                  padding: '12px',
                  borderRadius: '4px',
                  marginTop: '16px',
                  marginBottom: '16px',
                  border: '1px solid #ffa39e'
                }}>
                  <div style={{ fontSize: '13px', color: '#cf1322', marginBottom: 4 }}>
                    <strong><StopOutlined /> Alasan Penolakan Sebelumnya:</strong>
                  </div>
                  <div style={{ fontSize: '13px', color: '#8c8c8c' }}>
                    {selectedRecord.rejection_reason}
                  </div>
                </div>
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={handleApprove}
                className="verification-form"
              >
                <Form.Item
                  label="Role"
                  name="role"
                  rules={[{ required: true, message: 'Role wajib dipilih' }]}
                >
                  <Select
                    placeholder="Pilih role terlebih dahulu"
                    size="large"
                    onChange={handleRoleChange}
                  >
                    <Option value="student">Mahasiswa</Option>
                    <Option value="lecturer">Dosen</Option>
                    <Option value="staff">Tenaga Pendidik</Option>
                  </Select>
                </Form.Item>

                {selectedRole === 'student' && (
                  <Form.Item
                    label="NIM (9 digit)"
                    name="nim_nip"
                    rules={[
                      { required: true, message: 'NIM wajib diisi' },
                      { pattern: /^[0-9]{9}$/, message: 'NIM harus 9 digit angka' }
                    ]}
                  >
                    <Input
                      placeholder="Masukkan NIM 9 digit"
                      size="large"
                      maxLength={9}
                    />
                  </Form.Item>
                )}

                {(selectedRole === 'lecturer' || selectedRole === 'staff') && (
                  <>
                    <Form.Item
                      label={`Cari ${selectedRole === 'lecturer' ? 'Dosen' : 'Tenaga Pendidik'}`}
                      name="affiliate_search"
                      tooltip="Gunakan fitur pencarian untuk otomatis mengisi data dan unit"
                    >
                      <AutoComplete
                        options={affiliateOptions}
                        onSearch={handleAffiliateSearch}
                        onSelect={handleAffiliateSelect}
                        placeholder={`Ketik nama atau NIP ${selectedRole === 'lecturer' ? 'dosen' : 'tenaga pendidik'}...`}
                        size="large"
                        notFoundContent={searchingAffiliates ? <Spin size="small" /> : 'Tidak ada data'}
                      />
                    </Form.Item>

                    <div style={{
                      background: '#e6f7ff',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      marginBottom: '16px',
                      border: '1px solid #91d5ff',
                      fontSize: '13px'
                    }}>
                      💡 <strong>Tips:</strong> Gunakan pencarian di atas untuk otomatis mengisi NIP, Nama, dan Unit.
                      Jika tidak ditemukan, Anda dapat mengisi manual di bawah.
                    </div>

                    <Form.Item
                      label="NIP"
                      name="nim_nip"
                      rules={[
                        { required: true, message: 'NIP wajib diisi' },
                        { pattern: /^[0-9]+$/, message: 'Hanya boleh angka' }
                      ]}
                    >
                      <Input
                        placeholder="NIP akan terisi otomatis atau ketik manual"
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      label="Nama"
                      name="name"
                      rules={[{ required: true, message: 'Nama wajib diisi' }]}
                    >
                      <Input
                        placeholder="Nama akan terisi otomatis atau ketik manual"
                        size="large"
                      />
                    </Form.Item>
                  </>
                )}

                <div style={{
                  background: '#e6f7ff',
                  padding: '12px',
                  borderRadius: '4px',
                  marginBottom: '16px',
                  border: '1px solid #91d5ff'
                }}>
                  <div style={{ fontSize: '13px', color: '#0050b3' }}>
                    ℹ️ <strong>Scoring Otomatis:</strong> Setiap konten yang disetujui akan mendapat +1 poin secara otomatis.
                  </div>
                </div>

                <Form.Item className="form-actions">
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Button
                      danger
                      icon={<CloseCircleOutlined />}
                      onClick={() => {
                        setModalVisible(false);
                        handleReject(selectedRecord);
                      }}
                    >
                      Tolak Konten
                    </Button>
                    <Space>
                      <Button onClick={() => setModalVisible(false)}>
                        Batal
                      </Button>
                      <Button type="primary" htmlType="submit">
                        Verifikasi & Simpan
                      </Button>
                    </Space>
                  </Space>
                </Form.Item>
              </Form>
            </div>
          )}
        </Modal>

        {/* Rejection Modal */}
        <Modal
          title={
            <div className="modal-title">
              <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
              <span>Tolak Konten</span>
            </div>
          }
          open={rejectModalVisible}
          onCancel={() => setRejectModalVisible(false)}
          footer={null}
          width={600}
          className="verification-modal"
        >
          {selectedRecord && (
            <div className="modal-content">
              <div className="video-preview">
                <YoutubeOutlined className="preview-icon" />
                <div>
                  <h3>{selectedRecord.video_title}</h3>
                  <p>{selectedRecord.channel_name}</p>
                  <a
                    href={selectedRecord.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Lihat Content <EyeOutlined />
                  </a>
                </div>
              </div>

              <Form
                form={rejectForm}
                layout="vertical"
                onFinish={handleRejectSubmit}
                className="verification-form"
              >
                <Form.Item
                  label="Alasan Penolakan"
                  name="rejection_reason"
                  rules={[
                    { required: true, message: 'Alasan penolakan wajib diisi' },
                    { min: 10, message: 'Alasan minimal 10 karakter' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Jelaskan alasan penolakan konten ini..."
                    maxLength={500}
                    showCount
                  />
                </Form.Item>

                <Form.Item className="form-actions">
                  <Space>
                    <Button onClick={() => setRejectModalVisible(false)}>
                      Batal
                    </Button>
                    <Button type="primary" danger htmlType="submit">
                      Tolak Konten
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </div>
          )}
        </Modal>

        {/* Fetch Progress Modal */}
        <Modal
          title={
            <div className="modal-title">
              <DownloadOutlined />
              <span>Fetching New Data</span>
            </div>
          }
          open={fetchProgressModal}
          footer={null}
          closable={false}
          width={500}
        >
          <div style={{ padding: '20px 0' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <GoogleOutlined style={{ fontSize: 24, marginRight: 12, color: '#4285F4' }} />
                  <span style={{ flex: 1, fontWeight: 500 }}>Google Search</span>
                  {fetchStatus.google === 'loading' && <Spin size="small" />}
                  {fetchStatus.google === 'success' && <CheckCircleOutlined style={{ color: '#14B8A6', fontSize: 20 }} />}
                  {fetchStatus.google === 'error' && <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />}
                </div>
                <div style={{ fontSize: 12, color: '#666', marginLeft: 36 }}>
                  Fetching from: https://www.google.com/search?q={encodeURIComponent(selectedHashtag || '')}
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <YoutubeOutlined style={{ fontSize: 24, marginRight: 12, color: '#FF0000' }} />
                  <span style={{ flex: 1, fontWeight: 500 }}>YouTube Search</span>
                  {fetchStatus.youtube === 'loading' && <Spin size="small" />}
                  {fetchStatus.youtube === 'success' && <CheckCircleOutlined style={{ color: '#14B8A6', fontSize: 20 }} />}
                  {fetchStatus.youtube === 'error' && <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />}
                </div>
                <div style={{ fontSize: 12, color: '#666', marginLeft: 36 }}>
                  Fetching from: https://www.youtube.com/results?search_query={encodeURIComponent(selectedHashtag || '')}
                </div>
              </div>

              {(fetchStatus.google === 'success' || fetchStatus.youtube === 'success') && (
                <div style={{
                  background: '#e6f7ff',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #91d5ff'
                }}>
                  <div style={{ fontSize: 14, color: '#0050b3' }}>
                    ✓ Data fetch completed! Only new unique content will be saved.
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    Refreshing data in a few seconds...
                  </div>
                </div>
              )}
            </Space>
          </div>
        </Modal>

        {/* Manual Video Entry Modal */}
        <Modal
          title={
            <div className="modal-title">
              <CheckCircleOutlined style={{ color: '#14B8A6' }} />
              <span>Tambah Video Manual</span>
            </div>
          }
          open={manualEntryModalVisible}
          onCancel={() => setManualEntryModalVisible(false)}
          footer={null}
          width={600}
          className="verification-modal"
        >
          <div className="modal-content">
            <Form
              form={manualEntryForm}
              layout="vertical"
              onFinish={handleManualEntry}
              className="verification-form"
            >
              <Form.Item
                label="Platform"
                name="platform"
                rules={[{ required: true, message: 'Platform wajib dipilih' }]}
                initialValue="youtube"
              >
                <Select size="large">
                  <Option value="youtube"><YoutubeOutlined /> YouTube</Option>
                  <Option value="google"><GoogleOutlined /> Google</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="URL Video"
                name="content_url"
                rules={[
                  { required: true, message: 'URL video wajib diisi' },
                  { type: 'url', message: 'URL tidak valid' }
                ]}
              >
                <Input
                  placeholder="https://www.youtube.com/watch?v=..."
                  size="large"
                />
              </Form.Item>

              <Form.Item
                label="Judul Video"
                name="content_text"
                rules={[{ required: true, message: 'Judul video wajib diisi' }]}
              >
                <Input
                  placeholder="Masukkan judul video..."
                  size="large"
                  maxLength={200}
                />
              </Form.Item>

              <Form.Item
                label="Role"
                name="role"
                rules={[{ required: true, message: 'Role wajib dipilih' }]}
              >
                <Select
                  placeholder="Pilih role terlebih dahulu"
                  size="large"
                  onChange={handleRoleChange}
                >
                  <Option value="student">Mahasiswa</Option>
                  <Option value="lecturer">Dosen</Option>
                  <Option value="staff">Tenaga Pendidik</Option>
                </Select>
              </Form.Item>

              {selectedRole === 'student' && (
                <Form.Item
                  label="NIM (9 digit)"
                  name="nim_nip"
                  rules={[
                    { required: true, message: 'NIM wajib diisi' },
                    { pattern: /^[0-9]{9}$/, message: 'NIM harus 9 digit angka' }
                  ]}
                >
                  <Input
                    placeholder="Masukkan NIM 9 digit"
                    size="large"
                    maxLength={9}
                  />
                </Form.Item>
              )}

              {(selectedRole === 'lecturer' || selectedRole === 'staff') && (
                <>
                  <Form.Item
                    label={`Cari ${selectedRole === 'lecturer' ? 'Dosen' : 'Tenaga Pendidik'}`}
                    name="affiliate_search"
                  >
                    <AutoComplete
                      options={affiliateOptions}
                      onSearch={handleAffiliateSearch}
                      onSelect={handleManualEntryAffiliateSelect}
                      placeholder={`Ketik nama atau NIP ${selectedRole === 'lecturer' ? 'dosen' : 'tenaga pendidik'}...`}
                      size="large"
                      notFoundContent={searchingAffiliates ? <Spin size="small" /> : 'Tidak ada data'}
                    />
                  </Form.Item>

                  <Form.Item
                    label="NIP"
                    name="nim_nip"
                    rules={[
                      { required: true, message: 'NIP wajib diisi' },
                      { pattern: /^[0-9]+$/, message: 'Hanya boleh angka' }
                    ]}
                  >
                    <Input
                      placeholder="NIP akan terisi otomatis atau ketik manual"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Nama"
                    name="name"
                    rules={[{ required: true, message: 'Nama wajib diisi' }]}
                  >
                    <Input
                      placeholder="Nama akan terisi otomatis atau ketik manual"
                      size="large"
                    />
                  </Form.Item>
                </>
              )}

              <div style={{
                background: '#f6ffed',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
                border: '1px solid #b7eb8f'
              }}>
                <div style={{ fontSize: '13px', color: '#14B8A6' }}>
                  ✅ <strong>Auto-Verifikasi:</strong> Video akan langsung diverifikasi dan mendapat +1 poin secara otomatis.
                </div>
              </div>

              <Form.Item className="form-actions">
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={() => setManualEntryModalVisible(false)}>
                    Batal
                  </Button>
                  <Button type="primary" htmlType="submit" icon={<CheckCircleOutlined />}>
                    Tambah & Verifikasi
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        </Modal>
      </>
    );
  }

  // Standalone mode with full layout
  return (
    <div className="verification-container">
      <div className="verification-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Verification Dashboard</h1>
            <p>Verifikasi data hasil crawling dan tautkan ke NIM/NIP</p>
          </div>
          <div className="header-right">
            <Button
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              className="logout-btn"
            >
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="verification-content">
        <Card className="verification-card">
          {verificationContent}
        </Card>
      </div>

      {/* Verification Modal */}
      <Modal
        title={
          <div className="modal-title">
            <CheckCircleOutlined />
            <span>Verifikasi Video</span>
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
        className="verification-modal"
      >
        {selectedRecord && (
          <div className="modal-content">
            <div className="video-preview">
              <YoutubeOutlined className="preview-icon" />
              <div>
                <h3>{selectedRecord.video_title}</h3>
                <p>{selectedRecord.channel_name}</p>
                <a 
                  href={selectedRecord.video_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Lihat Video <EyeOutlined />
                </a>
              </div>
            </div>

            {/* Show rejection reason context when reverifying from rejected tab */}
            {activeTab === 'rejected' && selectedRecord.rejection_reason && (
              <div style={{
                background: '#fff1f0',
                padding: '12px',
                borderRadius: '4px',
                marginTop: '16px',
                marginBottom: '16px',
                border: '1px solid #ffa39e'
              }}>
                <div style={{ fontSize: '13px', color: '#cf1322', marginBottom: 4 }}>
                  <strong><StopOutlined /> Alasan Penolakan Sebelumnya:</strong>
                </div>
                <div style={{ fontSize: '13px', color: '#8c8c8c' }}>
                  {selectedRecord.rejection_reason}
                </div>
              </div>
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={handleApprove}
              className="verification-form"
            >
              <Form.Item
                label="Role"
                name="role"
                rules={[{ required: true, message: 'Role wajib dipilih' }]}
              >
                <Select
                  placeholder="Pilih role terlebih dahulu"
                  size="large"
                  onChange={handleRoleChange}
                >
                  <Option value="student">Mahasiswa</Option>
                  <Option value="lecturer">Dosen</Option>
                  <Option value="staff">Tenaga Pendidik</Option>
                </Select>
              </Form.Item>

              {selectedRole === 'student' && (
                <Form.Item
                  label="NIM (9 digit)"
                  name="nim_nip"
                  rules={[
                    { required: true, message: 'NIM wajib diisi' },
                    { pattern: /^[0-9]{9}$/, message: 'NIM harus 9 digit angka' }
                  ]}
                >
                  <Input
                    placeholder="Masukkan NIM 9 digit"
                    size="large"
                    maxLength={9}
                  />
                </Form.Item>
              )}

              {(selectedRole === 'lecturer' || selectedRole === 'staff') && (
                <>
                  <Form.Item
                    label={`Cari ${selectedRole === 'lecturer' ? 'Dosen' : 'Tenaga Pendidik'}`}
                    name="affiliate_search"
                    tooltip="Gunakan fitur pencarian untuk otomatis mengisi data dan unit"
                  >
                    <AutoComplete
                      options={affiliateOptions}
                      onSearch={handleAffiliateSearch}
                      onSelect={handleAffiliateSelect}
                      placeholder={`Ketik nama atau NIP ${selectedRole === 'lecturer' ? 'dosen' : 'tenaga pendidik'}...`}
                      size="large"
                      notFoundContent={searchingAffiliates ? <Spin size="small" /> : 'Tidak ada data'}
                    />
                  </Form.Item>

                  <div style={{
                    background: '#e6f7ff',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    marginBottom: '16px',
                    border: '1px solid #91d5ff',
                    fontSize: '13px'
                  }}>
                    💡 <strong>Tips:</strong> Gunakan pencarian di atas untuk otomatis mengisi NIP, Nama, dan Unit.
                    Jika tidak ditemukan, Anda dapat mengisi manual di bawah.
                  </div>

                  <Form.Item
                    label="NIP"
                    name="nim_nip"
                    rules={[
                      { required: true, message: 'NIP wajib diisi' },
                      { pattern: /^[0-9]+$/, message: 'Hanya boleh angka' }
                    ]}
                  >
                    <Input
                      placeholder="NIP akan terisi otomatis atau ketik manual"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Nama"
                    name="name"
                    rules={[{ required: true, message: 'Nama wajib diisi' }]}
                  >
                    <Input
                      placeholder="Nama akan terisi otomatis atau ketik manual"
                      size="large"
                    />
                  </Form.Item>
                </>
              )}

              <div style={{
                background: '#e6f7ff',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
                border: '1px solid #91d5ff'
              }}>
                <div style={{ fontSize: '13px', color: '#0050b3' }}>
                  ℹ️ <strong>Scoring Otomatis:</strong> Setiap konten yang disetujui akan mendapat +1 poin secara otomatis.
                </div>
              </div>

              <Form.Item className="form-actions">
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => {
                      setModalVisible(false);
                      handleReject(selectedRecord);
                    }}
                  >
                    Tolak Konten
                  </Button>
                  <Space>
                    <Button onClick={() => setModalVisible(false)}>
                      Batal
                    </Button>
                    <Button type="primary" htmlType="submit">
                      Verifikasi & Simpan
                    </Button>
                  </Space>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* Rejection Modal */}
      <Modal
        title={
          <div className="modal-title">
            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>Tolak Konten</span>
          </div>
        }
        open={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        footer={null}
        width={600}
        className="verification-modal"
      >
        {selectedRecord && (
          <div className="modal-content">
            <div className="video-preview">
              <YoutubeOutlined className="preview-icon" />
              <div>
                <h3>{selectedRecord.video_title}</h3>
                <p>{selectedRecord.channel_name}</p>
                <a
                  href={selectedRecord.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Lihat Content <EyeOutlined />
                </a>
              </div>
            </div>

            <Form
              form={rejectForm}
              layout="vertical"
              onFinish={handleRejectSubmit}
              className="verification-form"
            >
              <Form.Item
                label="Alasan Penolakan"
                name="rejection_reason"
                rules={[
                  { required: true, message: 'Alasan penolakan wajib diisi' },
                  { min: 10, message: 'Alasan minimal 10 karakter' }
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Jelaskan alasan penolakan konten ini..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <Form.Item className="form-actions">
                <Space>
                  <Button onClick={() => setRejectModalVisible(false)}>
                    Batal
                  </Button>
                  <Button type="primary" danger htmlType="submit">
                    Tolak Konten
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* Fetch Progress Modal */}
      <Modal
        title={
          <div className="modal-title">
            <DownloadOutlined />
            <span>Fetching New Data</span>
          </div>
        }
        open={fetchProgressModal}
        footer={null}
        closable={false}
        width={500}
      >
        <div style={{ padding: '20px 0' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <GoogleOutlined style={{ fontSize: 24, marginRight: 12, color: '#4285F4' }} />
                <span style={{ flex: 1, fontWeight: 500 }}>Google Search</span>
                {fetchStatus.google === 'loading' && <Spin size="small" />}
                {fetchStatus.google === 'success' && <CheckCircleOutlined style={{ color: '#14B8A6', fontSize: 20 }} />}
                {fetchStatus.google === 'error' && <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />}
              </div>
              <div style={{ fontSize: 12, color: '#666', marginLeft: 36 }}>
                Fetching from: https://www.google.com/search?q=%23AI-ITERA
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <YoutubeOutlined style={{ fontSize: 24, marginRight: 12, color: '#FF0000' }} />
                <span style={{ flex: 1, fontWeight: 500 }}>YouTube Search</span>
                {fetchStatus.youtube === 'loading' && <Spin size="small" />}
                {fetchStatus.youtube === 'success' && <CheckCircleOutlined style={{ color: '#14B8A6', fontSize: 20 }} />}
                {fetchStatus.youtube === 'error' && <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />}
              </div>
              <div style={{ fontSize: 12, color: '#666', marginLeft: 36 }}>
                Fetching from: https://www.youtube.com/results?search_query=%23AI-ITERA
              </div>
            </div>

            {(fetchStatus.google === 'success' || fetchStatus.youtube === 'success') && (
              <div style={{
                background: '#e6f7ff',
                padding: '12px',
                borderRadius: '4px',
                border: '1px solid #91d5ff'
              }}>
                <div style={{ fontSize: 14, color: '#0050b3' }}>
                  ✓ Data fetch completed! Only new unique content will be saved.
                </div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  Refreshing data in a few seconds...
                </div>
              </div>
            )}
          </Space>
        </div>
      </Modal>

      {/* History Modal */}
      <Modal
        title={
          <div className="modal-title">
            <HistoryOutlined style={{ color: '#14B8A6' }} />
            <span>Riwayat Verifikasi</span>
          </div>
        }
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHistoryModalVisible(false)}>
            Tutup
          </Button>
        ]}
        width={800}
        className="verification-modal"
      >
        <div className="modal-content">
          {selectedRecord && (
            <div style={{
              background: '#f0f2f5',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: 4 }}>
                {selectedRecord.video_title || selectedRecord.channel_name}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                <Tag color="blue">{selectedRecord.hashtag}</Tag>
                <Tag>{selectedRecord.platform}</Tag>
              </div>
            </div>
          )}

          {loadingHistory ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#666' }}>Memuat history...</div>
            </div>
          ) : historyData.length === 0 ? (
            <Empty description="Tidak ada riwayat verifikasi" />
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {historyData.map((history, index) => {
                const statusConfig = {
                  pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Pending' },
                  approved: { color: 'success', icon: <CheckCircleOutlined />, text: 'Disetujui' },
                  rejected: { color: 'error', icon: <CloseCircleOutlined />, text: 'Ditolak' }
                };
                const config = statusConfig[history.status] || statusConfig.pending;

                const isFirstEntry = index === historyData.length - 1;
                const isLatest = index === 0;

                return (
                  <div
                    key={history.verification_id}
                    style={{
                      padding: '16px',
                      marginBottom: '12px',
                      border: isLatest ? '2px solid #14B8A6' : '1px solid #d9d9d9',
                      borderRadius: '4px',
                      background: isLatest ? '#e6f7ff' : 'white',
                      position: 'relative'
                    }}
                  >
                    {isLatest && (
                      <div style={{
                        position: 'absolute',
                        top: -10,
                        right: 16,
                        background: '#14B8A6',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 500
                      }}>
                        Status Terkini
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                      <div>
                        <Tag icon={config.icon} color={config.color} style={{ marginBottom: 4 }}>
                          {config.text}
                        </Tag>
                        {!isFirstEntry && (
                          <Tag color="blue">
                            {isLatest ? 'Re-Verifikasi' : 'Perubahan Status'}
                          </Tag>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: '#999' }}>
                          {history.verified_at ? new Date(history.verified_at).toLocaleString('id-ID', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
                        <strong>Diverifikasi oleh:</strong> {history.verified_by || 'N/A'}
                      </div>

                      {history.score !== null && history.score !== undefined && (
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
                          <strong>Skor:</strong> +{history.score} poin
                        </div>
                      )}

                      {history.rejection_reason && (
                        <div style={{ fontSize: '12px', color: '#ff4d4f', marginTop: 8 }}>
                          <strong>Alasan Penolakan:</strong> {history.rejection_reason}
                        </div>
                      )}

                      {history.affiliates && history.affiliates.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
                            <strong>Kontributor:</strong>
                          </div>
                          <div>
                            {history.affiliates.map((aff, idx) => (
                              <Tag key={idx} color="green" style={{ marginBottom: 4 }}>
                                {aff.name || aff.external_id} ({aff.type})
                              </Tag>
                            ))}
                          </div>
                        </div>
                      )}

                      {history.updated_at && history.updated_at !== history.created_at && (
                        <div style={{ fontSize: '11px', color: '#999', marginTop: 8, fontStyle: 'italic' }}>
                          Terakhir diupdate: {new Date(history.updated_at).toLocaleString('id-ID', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Verification;
